import React, { useRef, useEffect, useCallback, useState } from 'react';
import { gsap } from 'gsap';
import './AdminDashboard.css'; // Siguraduhing nagawa mo yung CSS file para dito!

const DEFAULT_PARTICLE_COUNT = 12;
const DEFAULT_SPOTLIGHT_RADIUS = 400;
const DEFAULT_GLOW_COLOR = '0, 191, 134'; // Helport Green
const MOBILE_BREAKPOINT = 768;

const createParticleElement = (x, y, color = DEFAULT_GLOW_COLOR) => {
  const el = document.createElement('div');
  el.className = 'particle';
  el.style.cssText = `
    position: absolute; width: 4px; height: 4px; border-radius: 50%;
    background: rgba(${color}, 1); box-shadow: 0 0 8px rgba(${color}, 0.8);
    pointer-events: none; z-index: 100; left: ${x}px; top: ${y}px;
  `;
  return el;
};

const calculateSpotlightValues = radius => ({ proximity: radius * 0.5, fadeDistance: radius * 0.75 });

const updateCardGlowProperties = (card, mouseX, mouseY, glow, radius) => {
  const rect = card.getBoundingClientRect();
  const relativeX = ((mouseX - rect.left) / rect.width) * 100;
  const relativeY = ((mouseY - rect.top) / rect.height) * 100;
  card.style.setProperty('--glow-x', `${relativeX}%`);
  card.style.setProperty('--glow-y', `${relativeY}%`);
  card.style.setProperty('--glow-intensity', glow.toString());
  card.style.setProperty('--glow-radius', `${radius}px`);
};

// --- MAGIC BENTO INTERACTIVE CARD ---
const ParticleCard = ({ children, className = '', disableAnimations = false, particleCount = DEFAULT_PARTICLE_COUNT, glowColor = DEFAULT_GLOW_COLOR, enableTilt = true, clickEffect = true, enableMagnetism = false }) => {
  const cardRef = useRef(null);
  const particlesRef = useRef([]);
  const timeoutsRef = useRef([]);
  const isHoveredRef = useRef(false);
  const memoizedParticles = useRef([]);
  const particlesInitialized = useRef(false);

  const initializeParticles = useCallback(() => {
    if (particlesInitialized.current || !cardRef.current) return;
    const { width, height } = cardRef.current.getBoundingClientRect();
    memoizedParticles.current = Array.from({ length: particleCount }, () => createParticleElement(Math.random() * width, Math.random() * height, glowColor));
    particlesInitialized.current = true;
  }, [particleCount, glowColor]);

  const clearAllParticles = useCallback(() => {
    timeoutsRef.current.forEach(clearTimeout);
    timeoutsRef.current = [];
    particlesRef.current.forEach(particle => {
      gsap.to(particle, { scale: 0, opacity: 0, duration: 0.3, ease: 'back.in(1.7)', onComplete: () => particle.parentNode?.removeChild(particle) });
    });
    particlesRef.current = [];
  }, []);

  const animateParticles = useCallback(() => {
    if (!cardRef.current || !isHoveredRef.current) return;
    if (!particlesInitialized.current) initializeParticles();
    memoizedParticles.current.forEach((particle, index) => {
      const timeoutId = setTimeout(() => {
        if (!isHoveredRef.current || !cardRef.current) return;
        const clone = particle.cloneNode(true);
        cardRef.current.appendChild(clone);
        particlesRef.current.push(clone);
        gsap.fromTo(clone, { scale: 0, opacity: 0 }, { scale: 1, opacity: 1, duration: 0.3, ease: 'back.out(1.7)' });
        gsap.to(clone, { x: (Math.random() - 0.5) * 100, y: (Math.random() - 0.5) * 100, rotation: Math.random() * 360, duration: 2 + Math.random() * 2, ease: 'none', repeat: -1, yoyo: true });
        gsap.to(clone, { opacity: 0.3, duration: 1.5, ease: 'power2.inOut', repeat: -1, yoyo: true });
      }, index * 100);
      timeoutsRef.current.push(timeoutId);
    });
  }, [initializeParticles]);

  useEffect(() => {
    if (disableAnimations || !cardRef.current) return;
    const element = cardRef.current;
    
    const handleMouseEnter = () => {
      isHoveredRef.current = true;
      animateParticles();
      if (enableTilt) gsap.to(element, { rotateX: 2, rotateY: 2, duration: 0.3, ease: 'power2.out', transformPerspective: 1000 });
    };

    const handleMouseLeave = () => {
      isHoveredRef.current = false;
      clearAllParticles();
      if (enableTilt) gsap.to(element, { rotateX: 0, rotateY: 0, duration: 0.3, ease: 'power2.out' });
      if (enableMagnetism) gsap.to(element, { x: 0, y: 0, duration: 0.3, ease: 'power2.out' });
    };

    const handleMouseMove = e => {
      if (!enableTilt && !enableMagnetism) return;
      const rect = element.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      const centerX = rect.width / 2;
      const centerY = rect.height / 2;

      if (enableTilt) {
        const rotateX = ((y - centerY) / centerY) * -5;
        const rotateY = ((x - centerX) / centerX) * 5;
        gsap.to(element, { rotateX, rotateY, duration: 0.1, ease: 'power2.out', transformPerspective: 1000 });
      }
      if (enableMagnetism) gsap.to(element, { x: (x - centerX) * 0.05, y: (y - centerY) * 0.05, duration: 0.3, ease: 'power2.out' });
    };

    const handleClick = e => {
      if (!clickEffect) return;
      const rect = element.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      const maxDistance = Math.max(Math.hypot(x, y), Math.hypot(x - rect.width, y), Math.hypot(x, y - rect.height), Math.hypot(x - rect.width, y - rect.height));
      const ripple = document.createElement('div');
      ripple.style.cssText = `position: absolute; width: ${maxDistance * 2}px; height: ${maxDistance * 2}px; border-radius: 50%; background: radial-gradient(circle, rgba(${glowColor}, 0.3) 0%, rgba(${glowColor}, 0.1) 30%, transparent 70%); left: ${x - maxDistance}px; top: ${y - maxDistance}px; pointer-events: none; z-index: 1000;`;
      element.appendChild(ripple);
      gsap.fromTo(ripple, { scale: 0, opacity: 1 }, { scale: 1, opacity: 0, duration: 0.8, ease: 'power2.out', onComplete: () => ripple.remove() });
    };

    element.addEventListener('mouseenter', handleMouseEnter);
    element.addEventListener('mouseleave', handleMouseLeave);
    element.addEventListener('mousemove', handleMouseMove);
    element.addEventListener('click', handleClick);

    return () => {
      isHoveredRef.current = false;
      element.removeEventListener('mouseenter', handleMouseEnter);
      element.removeEventListener('mouseleave', handleMouseLeave);
      element.removeEventListener('mousemove', handleMouseMove);
      element.removeEventListener('click', handleClick);
      clearAllParticles();
    };
  }, [animateParticles, clearAllParticles, disableAnimations, enableTilt, enableMagnetism, clickEffect, glowColor]);

  return <div ref={cardRef} className={`particle-container ${className}`}>{children}</div>;
};

// --- SPOTLIGHT BACKGROUND EFFECT ---
const GlobalSpotlight = ({ gridRef, disableAnimations = false, enabled = true, spotlightRadius = DEFAULT_SPOTLIGHT_RADIUS, glowColor = DEFAULT_GLOW_COLOR }) => {
  const spotlightRef = useRef(null);

  useEffect(() => {
    if (disableAnimations || !gridRef?.current || !enabled) return;
    const spotlight = document.createElement('div');
    spotlight.className = 'global-spotlight';
    spotlight.style.cssText = `position: fixed; width: 800px; height: 800px; border-radius: 50%; pointer-events: none; background: radial-gradient(circle, rgba(${glowColor}, 0.15) 0%, rgba(${glowColor}, 0.08) 15%, transparent 70%); z-index: 200; opacity: 0; transform: translate(-50%, -50%);`;
    document.body.appendChild(spotlight);
    spotlightRef.current = spotlight;

    const handleMouseMove = e => {
      if (!spotlightRef.current || !gridRef.current) return;
      const section = gridRef.current.closest('.bento-section');
      const rect = section?.getBoundingClientRect();
      const mouseInside = rect && e.clientX >= rect.left && e.clientX <= rect.right && e.clientY >= rect.top && e.clientY <= rect.bottom;
      const cards = gridRef.current.querySelectorAll('.magic-bento-card');

      if (!mouseInside) {
        gsap.to(spotlightRef.current, { opacity: 0, duration: 0.3 });
        cards.forEach(card => card.style.setProperty('--glow-intensity', '0'));
        return;
      }

      const { proximity, fadeDistance } = calculateSpotlightValues(spotlightRadius);
      let minDistance = Infinity;

      cards.forEach(card => {
        const cardRect = card.getBoundingClientRect();
        const centerX = cardRect.left + cardRect.width / 2;
        const centerY = cardRect.top + cardRect.height / 2;
        const distance = Math.hypot(e.clientX - centerX, e.clientY - centerY) - Math.max(cardRect.width, cardRect.height) / 2;
        const effectiveDistance = Math.max(0, distance);
        minDistance = Math.min(minDistance, effectiveDistance);

        let glowIntensity = 0;
        if (effectiveDistance <= proximity) glowIntensity = 1;
        else if (effectiveDistance <= fadeDistance) glowIntensity = (fadeDistance - effectiveDistance) / (fadeDistance - proximity);
        updateCardGlowProperties(card, e.clientX, e.clientY, glowIntensity, spotlightRadius);
      });

      gsap.to(spotlightRef.current, { left: e.clientX, top: e.clientY, duration: 0.1 });
      const targetOpacity = minDistance <= proximity ? 0.8 : minDistance <= fadeDistance ? ((fadeDistance - minDistance) / (fadeDistance - proximity)) * 0.8 : 0;
      gsap.to(spotlightRef.current, { opacity: targetOpacity, duration: targetOpacity > 0 ? 0.2 : 0.5 });
    };

    const handleMouseLeave = () => {
      gridRef.current?.querySelectorAll('.magic-bento-card').forEach(card => card.style.setProperty('--glow-intensity', '0'));
      if (spotlightRef.current) gsap.to(spotlightRef.current, { opacity: 0, duration: 0.3 });
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseleave', handleMouseLeave);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseleave', handleMouseLeave);
      spotlightRef.current?.parentNode?.removeChild(spotlightRef.current);
    };
  }, [gridRef, disableAnimations, enabled, spotlightRadius, glowColor]);

  return null;
};

// --- MISMONG ADMIN DASHBOARD UI ---
const AdminDashboard = ({ allLogs, registeredEmployees, getDailyAnalytics }) => {
  const gridRef = useRef(null);
  const [isMobile, setIsMobile] = useState(false);
  
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= MOBILE_BREAKPOINT);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const activeCount = allLogs.filter(l => !l.timeOut || l.timeOut === "" || l.timeOut === "--:--").length;

  // --- BAGONG LOGIC PARA SA MONDAY TO SUNDAY (7 DAYS) ---
  const getAnalyticsData = () => {
    // Naka-set up ang 7 araw mula Mon hanggang Sun
    const weeklyCount = { Mon: 0, Tue: 0, Wed: 0, Thu: 0, Fri: 0, Sat: 0, Sun: 0 };
    const orderedDays = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

    (allLogs || []).forEach(log => {
      // Kinukuha ang date field mula sa logs para mabilang
      const logDate = new Date(log.date || log.timestamp || log.timeIn);
      if (!isNaN(logDate.getTime())) {
        const dayName = logDate.toLocaleDateString('en-US', { weekday: 'short' });
        if (weeklyCount.hasOwnProperty(dayName)) {
          weeklyCount[dayName]++;
        }
      }
    });

    return orderedDays.map(day => ({ dayName: day, count: weeklyCount[day] }));
  };

  // Pre-calculate para mas mabilis mag-load at hindi maging laggy
  const analyticsData = getAnalyticsData();
  const maxLogs = Math.max(...analyticsData.map(d => d.count), 1);

  return (
    <div className="dashboard-page">
      <div className="dashboard-page-header">
        <div className="dashboard-page-badge">
          <span className="dashboard-page-badge-dot" />
          Analytics
        </div>
        <h2 className="dashboard-page-title">Dashboard Overview</h2>
        <p className="dashboard-page-subtitle">Real-time workforce and attendance insights</p>
      </div>

      <GlobalSpotlight gridRef={gridRef} glowColor={DEFAULT_GLOW_COLOR} />

      <div className="card-grid bento-section" ref={gridRef}>

        <ParticleCard className="magic-bento-card magic-bento-card--border-glow" disableAnimations={isMobile}>
          <div className="magic-bento-card__header"><span>Insights</span></div>
          <div className="magic-bento-card__stat">
            <span className="magic-bento-stat-num">{allLogs.length}</span>
          </div>
          <div className="magic-bento-card__footer">
            <h2 className="magic-bento-card__title">Total Logs</h2>
            <p className="magic-bento-card__description">All recorded attendance</p>
          </div>
        </ParticleCard>

        <ParticleCard className="magic-bento-card magic-bento-card--border-glow" disableAnimations={isMobile}>
          <div className="magic-bento-card__header"><span>Overview</span></div>
          <div className="magic-bento-card__stat">
            <span className="magic-bento-stat-num">{activeCount}</span>
          </div>
          <div className="magic-bento-card__footer">
            <h2 className="magic-bento-card__title">Active Logs</h2>
            <p className="magic-bento-card__description">Currently on-duty personnel</p>
          </div>
        </ParticleCard>

        <ParticleCard className="magic-bento-card magic-bento-card--border-glow" disableAnimations={isMobile}>
          <div className="magic-bento-card__header"><span>Workforce</span></div>
          <div className="magic-bento-card__stat magic-bento-card__stat--center">
            <span className="magic-bento-stat-num magic-bento-stat-num--large">{registeredEmployees.length}</span>
          </div>
          <div className="magic-bento-card__footer">
            <h2 className="magic-bento-card__title">Registered</h2>
            <p className="magic-bento-card__description">Total integrated profiles</p>
          </div>
        </ParticleCard>

        <ParticleCard className="magic-bento-card magic-bento-card--border-glow" disableAnimations={isMobile}>
          <div className="magic-bento-card__header"><span>Efficiency</span></div>

          <div className="bento-chart">
            {analyticsData.map((data, index) => (
              <div key={index} className="bento-chart-col">
                <span className="bento-chart-value">{data.count}</span>
                <div className="bento-chart-bar-wrap">
                  <div
                    className="bento-chart-bar"
                    style={{ height: `${Math.max((data.count / maxLogs) * 100, 4)}%` }}
                  />
                </div>
                <span className="bento-chart-day">{data.dayName}</span>
              </div>
            ))}
          </div>

          <div className="magic-bento-card__footer">
            <h2 className="magic-bento-card__title">Analytics</h2>
            <p className="magic-bento-card__description">Weekly attendance overview</p>
          </div>
        </ParticleCard>

      </div>
    </div>
  );
};

export default AdminDashboard;